import type * as THREE from 'three';
import type {SketchPointObject} from '../../mesh/sketch';
import {SketchTrimGeometryService} from './SketchTrimGeometryService';
import {SketchTrimEndpointRole} from './SketchTrimTypes';
import type {SketchTrimCurveObject} from './SketchTrimTypes';

/**
 * Corner 端点拓扑服务。
 *
 * 职责：
 * 1. 处理两条已选开放曲线在虚拟角点处的端点复用、断开与合并；
 * 2. 保护仍被未选实体引用的共享点，禁止 Corner 移动第三条实体；
 * 3. 两侧端点都受外部拓扑保护时，通过注入的点工厂创建独立角点；
 * 4. 不负责选择、求交、智能尺寸或历史事务，由控制器统一编排。
 */
export class SketchTrimCornerEndpointTopologyService {
    /** 创建新角点的工厂。 */
    private readonly createControlPoint: (
        position: THREE.Vector3,
        referenceCurve: SketchTrimCurveObject,
    ) => SketchPointObject;

    /** 将新角点挂入活动草图的回调。 */
    private readonly attachControlPoint: (
        point: SketchPointObject,
        referenceCurve: SketchTrimCurveObject,
    ) => void;

    /** 同步角点交互材质快照的回调。 */
    private readonly syncMaterialSnapshot: (point: SketchPointObject) => void;

    /**
     * 创建 Corner 端点拓扑服务。
     *
     * @param createControlPoint 控制点创建依赖。
     * @param attachControlPoint 控制点挂载依赖；参考线用于保持草图容器层级。
     * @param syncMaterialSnapshot 交互材质快照同步依赖。
     */
    public constructor(
        createControlPoint: (
            position: THREE.Vector3,
            referenceCurve: SketchTrimCurveObject,
        ) => SketchPointObject,
        attachControlPoint: (
            point: SketchPointObject,
            referenceCurve: SketchTrimCurveObject,
        ) => void,
        syncMaterialSnapshot: (point: SketchPointObject) => void,
    ) {
        this.createControlPoint = createControlPoint;
        this.attachControlPoint = attachControlPoint;
        this.syncMaterialSnapshot = syncMaterialSnapshot;
    }

    /**
     * 将两条已选开放曲线的指定端点合并到虚拟角点。
     *
     * 处理规则：
     * 1. 两端已经引用同一点时无需改绑，只同步交点位置；
     * 2. 优先复用没有未选外部连边的端点，避免拖动第三条实体；
     * 3. 两端都连接了未选实体时新建角点，只把两条已选线改绑到新点；
     * 4. 被替换端点仅在完全孤立后移除，仍被其它实体引用的点继续保留。
     *
     * @param firstCurve 第一条已选开放曲线。
     * @param firstEndpointRole 第一条曲线参与 Corner 的端点角色。
     * @param secondCurve 第二条已选开放曲线。
     * @param secondEndpointRole 第二条曲线参与 Corner 的端点角色。
     * @param intersectionPoint 两条自然延伸曲线的虚拟交点。
     * @returns 两条已选曲线最终共享的角点对象；端点协议不完整时返回 null。
     */
    public mergeToIntersection(
        firstCurve: SketchTrimCurveObject,
        firstEndpointRole: SketchTrimEndpointRole,
        secondCurve: SketchTrimCurveObject,
        secondEndpointRole: SketchTrimEndpointRole,
        intersectionPoint: THREE.Vector3,
    ): SketchPointObject | null {
        const firstCornerPoint = this.resolveEndpointPoint(firstCurve, firstEndpointRole);
        const secondCornerPoint = this.resolveEndpointPoint(secondCurve, secondEndpointRole);
        if (!firstCornerPoint || !secondCornerPoint) {
            return null;
        }

        if (firstCornerPoint === secondCornerPoint) {
            const toleranceSquared = SketchTrimGeometryService.ENDPOINT_MERGE_POSITION_TOLERANCE ** 2;
            const isAlreadyAtIntersection = firstCornerPoint.getPosition().distanceToSquared(intersectionPoint)
                <= toleranceSquared;
            if (isAlreadyAtIntersection) {
                // 已相交时禁止重复写点位，避免同点还连接第三条实体时触发无意义的联动刷新。
                this.syncMaterialSnapshot(firstCornerPoint);
                return firstCornerPoint;
            }
            if (this.hasOnlySelectedCurveConnections(firstCornerPoint, firstCurve, secondCurve)) {
                // 没有未选外部连边时允许修正数值误差，并继续复用既有拓扑点。
                firstCornerPoint.setPosition(intersectionPoint.clone());
                this.syncMaterialSnapshot(firstCornerPoint);
                return firstCornerPoint;
            }
        }

        const sharedCornerPoint = this.resolveReusableCornerPoint(
            firstCornerPoint,
            secondCornerPoint,
            firstCurve,
            secondCurve,
        ) ?? this.createIndependentCornerPoint(intersectionPoint, firstCurve);

        /**
         * 曲线对象的端点替换协议可能立即读取新点坐标并重建自身定义。
         * 因此必须先把安全复用点写到最终交点，再执行任一曲线的端点改绑；
         * 否则直线先选时，后选圆弧会使用直线旧端点重建出错误的大圆弧。
         */
        sharedCornerPoint.setPosition(intersectionPoint.clone());

        // 只替换两条已选曲线的角点端引用；未选实体仍保留原共享点对象和原坐标。
        if (sharedCornerPoint !== firstCornerPoint) {
            if (!this.replaceEndpointPoint(firstCurve, firstEndpointRole, sharedCornerPoint)) {
                return null;
            }
        }
        if (sharedCornerPoint !== secondCornerPoint) {
            if (!this.replaceEndpointPoint(secondCurve, secondEndpointRole, sharedCornerPoint)) {
                return null;
            }
        }

        this.removeDetachedPoint(firstCornerPoint, sharedCornerPoint);
        this.removeDetachedPoint(secondCornerPoint, sharedCornerPoint);
        this.syncMaterialSnapshot(sharedCornerPoint);
        return sharedCornerPoint;
    }

    /**
     * 解析可安全复用的角点。
     *
     * @param firstCornerPoint 第一条开放曲线原角点。
     * @param secondCornerPoint 第二条开放曲线原角点。
     * @param firstCurve 第一条已选开放曲线。
     * @param secondCurve 第二条已选开放曲线。
     * @returns 可安全移动的既有点；两端都受未选实体保护时返回 null。
     */
    private resolveReusableCornerPoint(
        firstCornerPoint: SketchPointObject,
        secondCornerPoint: SketchPointObject,
        firstCurve: SketchTrimCurveObject,
        secondCurve: SketchTrimCurveObject,
    ): SketchPointObject | null {
        if (this.hasOnlySelectedCurveConnections(firstCornerPoint, firstCurve, secondCurve)) {
            return firstCornerPoint;
        }
        if (this.hasOnlySelectedCurveConnections(secondCornerPoint, firstCurve, secondCurve)) {
            return secondCornerPoint;
        }
        return null;
    }

    /**
     * 判断点是否只连接本次 Corner 的两条已选开放曲线。
     *
     * @param point 待判断端点。
     * @param firstCurve 第一条已选开放曲线。
     * @param secondCurve 第二条已选开放曲线。
     * @returns 没有未选外部连边时返回 true。
     */
    private hasOnlySelectedCurveConnections(
        point: SketchPointObject,
        firstCurve: SketchTrimCurveObject,
        secondCurve: SketchTrimCurveObject,
    ): boolean {
        return point.getConnectedEdgesSnapshot().every((edge) => (
            edge === firstCurve || edge === secondCurve
        ));
    }

    /**
     * 创建并挂载独立角点。
     *
     * @param intersectionPoint 虚拟角点坐标。
     * @param referenceCurve 用于确定草图参考面的开放曲线。
     * @returns 已挂入活动草图的新控制点。
     */
    private createIndependentCornerPoint(
        intersectionPoint: THREE.Vector3,
        referenceCurve: SketchTrimCurveObject,
    ): SketchPointObject {
        const point = this.createControlPoint(intersectionPoint, referenceCurve);
        this.attachControlPoint(point, referenceCurve);
        return point;
    }

    /**
     * 解析端点角色对应的控制点。
     *
     * @param curve 目标开放曲线。
     * @param endpointRole 端点角色。
     * @returns 对应控制点；端点访问协议缺失时返回 null。
     */
    private resolveEndpointPoint(
        curve: SketchTrimCurveObject,
        endpointRole: SketchTrimEndpointRole,
    ): SketchPointObject | null {
        const endpointCurve = curve as SketchTrimCurveObject & {
            startPoint?: SketchPointObject;
            endPoint?: SketchPointObject;
            getStartPoint?: () => SketchPointObject;
            getEndPoint?: () => SketchPointObject;
        };
        if (endpointRole === SketchTrimEndpointRole.Start) {
            const point = endpointCurve.getStartPoint?.() ?? endpointCurve.startPoint;
            if (point) {
                return point;
            }
        } else {
            const point = endpointCurve.getEndPoint?.() ?? endpointCurve.endPoint;
            if (point) {
                return point;
            }
        }

        console.error(`[SketchTrimCornerEndpointTopologyService] Corner 端点解析失败：实体 ${curve.uuid} 未提供端点访问协议。`);
        return null;
    }

    /**
     * 替换开放曲线指定端点。
     *
     * @param curve 目标开放曲线。
     * @param endpointRole 端点角色。
     * @param point 新端点。
     * @returns 替换成功返回 true；端点替换协议缺失时返回 false。
     */
    private replaceEndpointPoint(
        curve: SketchTrimCurveObject,
        endpointRole: SketchTrimEndpointRole,
        point: SketchPointObject,
    ): boolean {
        const endpointCurve = curve as SketchTrimCurveObject & {
            setStartPoint?: (nextPoint: SketchPointObject) => void;
            setEndPoint?: (nextPoint: SketchPointObject) => void;
        };

        if (endpointRole === SketchTrimEndpointRole.Start) {
            if (!endpointCurve.setStartPoint) {
                console.error(`[SketchTrimCornerEndpointTopologyService] Corner 端点替换失败：实体 ${curve.uuid} 未提供起点替换协议。`);
                return false;
            }
            endpointCurve.setStartPoint(point);
            return true;
        }
        if (!endpointCurve.setEndPoint) {
            console.error(`[SketchTrimCornerEndpointTopologyService] Corner 端点替换失败：实体 ${curve.uuid} 未提供终点替换协议。`);
            return false;
        }
        endpointCurve.setEndPoint(point);
        return true;
    }

    /**
     * 删除已经完全脱离草图边的旧端点。
     *
     * @param point 待清理旧端点。
     * @param sharedCornerPoint 最终共享角点。
     */
    private removeDetachedPoint(
        point: SketchPointObject,
        sharedCornerPoint: SketchPointObject,
    ): void {
        if (point === sharedCornerPoint || point.getConnectedEdgesSnapshot().length > 0) {
            return;
        }
        if (point.parent) {
            point.parent.remove(point);
        }
    }
}
